// ============================================
// Access Control Hooks - TanStack Query
// ============================================

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { isApiError } from '@conai/api-client';
import type {
  AccessControlStatsResponse,
  AccessTokenListParams,
  AccessEventListParams,
  ShoppingSessionListParams,
  GenerateAccessTokenRequest,
  LogAccessEventRequest,
  UUID,
} from '@conai/shared-types';
import { useAccessControlService } from '../providers/ApiProvider';
import { queryKeys } from '../config/query-client';

// ============================================
// Access Tokens (QR Entry)
// ============================================

/**
 * Hook to list access tokens with pagination and filters
 */
export function useAccessTokens(params: AccessTokenListParams = {}) {
  const accessControlService = useAccessControlService();

  return useQuery({
    queryKey: queryKeys.accessControl.tokenList(params as Record<string, unknown>),
    queryFn: () => accessControlService.listTokens(params),
  });
}

/**
 * Hook to get active tokens for a customer
 */
export function useActiveTokensForCustomer(customerId: UUID | undefined) {
  const accessControlService = useAccessControlService();

  return useQuery({
    queryKey: queryKeys.accessControl.tokenList({ customer: customerId, is_active: true }),
    queryFn: () => accessControlService.getActiveTokensForCustomer(customerId!),
    enabled: !!customerId,
  });
}

/**
 * Hook to generate a new access token
 */
export function useGenerateAccessToken() {
  const accessControlService = useAccessControlService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: GenerateAccessTokenRequest) => accessControlService.generateToken(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.accessControl.tokens() });
      toast.success('Access token generated');
    },
  });
}

/**
 * Hook to validate an access token
 */
export function useValidateAccessToken(token: string | undefined, options?: { enabled?: boolean }) {
  const accessControlService = useAccessControlService();

  return useQuery({
    queryKey: [...queryKeys.accessControl.tokens(), 'validate', token],
    queryFn: () => accessControlService.validateToken(token!),
    enabled: options?.enabled !== false && !!token,
  });
}

// ============================================
// Access Events
// ============================================

/**
 * Hook to list access events with pagination and filters
 */
export function useAccessEvents(params: AccessEventListParams = {}) {
  const accessControlService = useAccessControlService();

  return useQuery({
    queryKey: queryKeys.accessControl.eventList(params as Record<string, unknown>),
    queryFn: () => accessControlService.listEvents(params),
  });
}

/**
 * Hook to get access events for a store
 */
export function useStoreAccessEvents(
  storeId: UUID | undefined,
  params: Omit<AccessEventListParams, 'store'> = {}
) {
  const accessControlService = useAccessControlService();

  return useQuery({
    queryKey: queryKeys.accessControl.eventList({ ...params, store: storeId }),
    queryFn: () => accessControlService.getStoreEvents(storeId!, params),
    enabled: !!storeId,
  });
}

/**
 * Hook to log an access event
 */
export function useLogAccessEvent() {
  const accessControlService = useAccessControlService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: LogAccessEventRequest) => accessControlService.logEvent(data),
    onSuccess: (event) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.accessControl.events() });
      queryClient.invalidateQueries({ queryKey: queryKeys.accessControl.sessions() });
      toast.success(`Access event logged: ${event.event_type}`);
    },
  });
}

// ============================================
// Shopping Sessions
// ============================================

/**
 * Hook to list shopping sessions with pagination and filters
 */
export function useShoppingSessions(params: ShoppingSessionListParams = {}) {
  const accessControlService = useAccessControlService();

  return useQuery({
    queryKey: queryKeys.accessControl.sessionList(params as Record<string, unknown>),
    queryFn: () => accessControlService.listSessions(params),
  });
}

/**
 * Hook to get a shopping session by ID
 */
export function useShoppingSession(id: string, options?: { enabled?: boolean }) {
  const accessControlService = useAccessControlService();

  return useQuery({
    queryKey: queryKeys.accessControl.sessionDetail(id),
    queryFn: () => accessControlService.getSessionById(id),
    enabled: options?.enabled !== false && !!id,
  });
}

/**
 * Hook to get shopping sessions for a store
 */
export function useStoreShoppingSessions(
  storeId: UUID | undefined,
  params: Omit<ShoppingSessionListParams, 'store'> = {}
) {
  const accessControlService = useAccessControlService();

  return useQuery({
    queryKey: queryKeys.accessControl.sessionList({ ...params, store: storeId }),
    queryFn: () => accessControlService.getStoreSessions(storeId!, params),
    enabled: !!storeId,
  });
}

/**
 * Hook to get shopping sessions for a customer
 */
export function useCustomerShoppingSessions(
  customerId: UUID | undefined,
  params: Omit<ShoppingSessionListParams, 'customer'> = {}
) {
  const accessControlService = useAccessControlService();

  return useQuery({
    queryKey: queryKeys.accessControl.sessionList({ ...params, customer: customerId }),
    queryFn: () => accessControlService.getCustomerSessions(customerId!, params),
    enabled: !!customerId,
  });
}

/**
 * Hook to check if customer is currently in store
 */
export function useIsCustomerInStore(customerId: UUID | undefined, storeId: UUID | undefined) {
  const accessControlService = useAccessControlService();

  return useQuery({
    queryKey: [...queryKeys.accessControl.sessions(), 'in-store', customerId, storeId],
    queryFn: () => accessControlService.isCustomerInStore(customerId!, storeId!),
    enabled: !!customerId && !!storeId,
  });
}

// ============================================
// Access Control Stats
// ============================================

/** Số nguyên không âm, hoặc `0` — chặn `null`/chuỗi/`NaN` lọt lên thẻ stat. */
function toCount(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number.parseInt(value, 10);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
}

function toIsoText(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

/**
 * Lỗi mà thử lại BAO NHIÊU LẦN CŨNG THẾ.
 *
 *   - 403: quyết định về quyền, không đổi trong phiên này.
 *   - 404: bản BE đang chạy chưa có endpoint (QA trước khi deploy).
 *
 * Dùng chung cho `retry` (chặn thử lại) và `refetchInterval` (chặn polling) —
 * hai thứ khác nhau, và chỉ chặn cái đầu là vẫn bắn request lỗi mỗi phút.
 */
function isPermanentFailure(error: unknown): boolean {
  const status = isApiError(error) ? error.status : undefined;
  return status === 403 || status === 404;
}

export interface AccessControlStatsResult {
  stats: {
    todayVisitors: number;
    currentlyInStore: number;
    todayEntries: number;
    todayExits: number;
  };
  /** IANA timezone BE dùng để cắt "hôm nay". `null` = `Store.timezone` chưa set. */
  timezone: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  /** `null` khi chưa có payload hợp lệ (đang tải / 403 / endpoint chưa có). */
  storeCount: number | null;
  /** Đã nhận được payload thật -> mới được phép tin 4 con số. */
  hasStats: boolean;
  /** 403: ngoài phạm vi HOẶC store không tồn tại — BE cố ý không phân biệt. */
  isForbidden: boolean;
  /** 404: bản BE đang chạy chưa có endpoint stats (QA trước khi deploy). */
  isUnavailable: boolean;
  isLoading: boolean;
  error: unknown;
  refetch: () => void;
}

/**
 * Thống kê ra/vào cho màn Access Control.
 *
 * `GET /access/sessions/stats/` làm hết phần tính toán, kể cả việc cắt mốc ngày
 * theo MÚI GIỜ CỦA STORE. FE không tự dựng "hôm nay" từ giờ trình duyệt nữa —
 * đó chính là lý do phải đọc lại `timezone`/`period_*` để nói rõ trên UI đang
 * xem theo múi giờ nào.
 *
 * `storeId` TUỲ CHỌN: bỏ trống = tổng mọi store user quản lý. Query KHÔNG còn
 * `enabled: !!storeId` — chính cái đó khoá vĩnh viễn 3 query cũ khiến 4 thẻ
 * luôn hiện 0.
 */
export function useAccessControlStats(storeId?: UUID): AccessControlStatsResult {
  const accessControlService = useAccessControlService();

  const query = useQuery({
    queryKey: [...queryKeys.accessControl.sessions(), 'stats', storeId ?? 'all'],
    queryFn: () => accessControlService.getSessionStats(storeId ? { store: storeId } : undefined),
    /**
     * Số sống (`currently_in_store`) nên làm mới đều đặn — TRỪ khi query đang
     * hỏng vì 403/404.
     *
     * `retry` ở dưới chỉ chặn thử lại TRONG MỘT lần fetch, nó KHÔNG chặn
     * polling. Trên QA chưa deploy `/access/sessions/stats/`, mở màn Access
     * Control là bắn một request 404 MỖI PHÚT suốt thời gian còn mở tab — làm
     * bẩn Network tab và console đúng lúc tester đang soi thứ khác.
     *
     * react-query v5 cho `refetchInterval` nhận hàm `(query) => number | false`
     * (user-app cũng dùng dạng này ở `app/_layout.tsx`). Trả `false` = dừng hẳn
     * polling; query vẫn refetch lại bình thường khi component remount hoặc khi
     * gọi `refetch()` tay, nên BE deploy xong là tự chạy lại.
     */
    refetchInterval: (query) => (isPermanentFailure(query.state.error) ? false : 60 * 1000),
    // 403 = quyết định về quyền, 404 = endpoint chưa deploy. Cả hai retry đều
    // vô nghĩa, chỉ tốn 3 request lỗi mỗi lần vào trang.
    retry: (failureCount, error) => {
      if (isPermanentFailure(error)) return false;
      return failureCount < 2;
    },
  });

  const status = isApiError(query.error) ? query.error.status : undefined;
  const isForbidden = status === 403;
  const isUnavailable = status === 404;

  const raw = query.data as Partial<AccessControlStatsResponse> | null | undefined;
  const hasStats = !!raw && typeof raw === 'object' && !Array.isArray(raw);

  return {
    stats: {
      todayVisitors: toCount(raw?.todays_visitors),
      currentlyInStore: toCount(raw?.currently_in_store),
      todayEntries: toCount(raw?.todays_entries),
      todayExits: toCount(raw?.todays_exits),
    },
    timezone: toIsoText(raw?.timezone),
    periodStart: toIsoText(raw?.period_start),
    periodEnd: toIsoText(raw?.period_end),
    // Phân biệt "chưa biết" (null) với "user không có store nào" (0) — hai thứ
    // này hiển thị khác nhau trên UI.
    storeCount: hasStats && raw && raw.store_count != null ? toCount(raw.store_count) : null,
    hasStats,
    isForbidden,
    isUnavailable,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}
